/**
 * Every date and time the portal shows is in IST, whatever timezone the browser
 * is set to. The API stores and sends UTC, so a plain toLocaleDateString() would
 * follow the browser and two users could see different days for the same record.
 *
 * A date with no time ("2026-10-30") parses as UTC midnight, which is 05:30 IST
 * on that same date, so date-only values never move to another day.
 *
 * Use these for display only. localDateString() fills date inputs.
 */

const IST = 'Asia/Kolkata';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// en-US numeric parts: en-IN spells the short month "Sept" and the period "pm".
const partsFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: IST,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
});

const toDate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const istParts = (value) => {
    const date = toDate(value);
    if (!date) return null;
    return Object.fromEntries(partsFormatter.formatToParts(date).map((p) => [p.type, p.value]));
};

/** For example `30 Oct 2026`. */
export const formatIstDate = (value, fallback = '—') => {
    const p = istParts(value);
    return p ? `${p.day.padStart(2, '0')} ${MONTHS[Number(p.month) - 1]} ${p.year}` : fallback;
};

/** For example `2:05 PM`. */
export const formatIstTime = (value, fallback = '') => {
    const p = istParts(value);
    return p ? `${p.hour}:${p.minute} ${p.dayPeriod}` : fallback;
};

/** For example `30 Oct 2026, 2:05 PM`. */
export const formatIstDateTime = (value, fallback = '—') => {
    const date = toDate(value);
    return date ? `${formatIstDate(date)}, ${formatIstTime(date)}` : fallback;
};

/** A deliberately different shape, such as `30 Oct` or `Friday`, still in IST. */
export const formatIstCustom = (value, options, fallback = '—') => {
    const date = toDate(value);
    return date
        ? new Intl.DateTimeFormat('en-IN', { ...options, timeZone: IST }).format(date)
        : fallback;
};
